/**
 * Listino comparator — confronta un materiale ancora con le alternative
 * di altri fornitori (o stesso fornitore) che corrispondono per EAN o
 * specifiche tecniche.
 *
 * Mostra una tabella side-by-side con prezzo netto, listino, fornitore,
 * delta % rispetto all'ancora. Utile per "trovo lo stesso prodotto a 0.40€
 * in meno da Knauf?".
 *
 * Trigger: voce "Confronta" nel menu azioni di MaterialsTable (icona Scale).
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingDown, TrendingUp, ShieldCheck, Barcode } from 'lucide-react';
import { useMaterialAlternatives, type MaterialAlternative } from '@/hooks/useMaterialAlternatives';
import type { DatabaseMaterial } from '@/hooks/useMaterials';
import { formatNumber } from '@/utils/format';

interface MaterialComparatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchor: DatabaseMaterial | null;
}

function euro(v: number | null | undefined, decimals = 2): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `€ ${formatNumber(v, decimals)}`;
}

function netOf(m: { net_price?: number | null; unit_price?: number | null; list_price?: number | null }): number | null {
  const v = m.net_price ?? m.unit_price ?? m.list_price;
  return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
}

const MaterialComparatorDialog: React.FC<MaterialComparatorDialogProps> = ({
  open, onOpenChange, anchor,
}) => {
  const { data: alternatives = [], isLoading } = useMaterialAlternatives(anchor);

  if (!anchor) return null;

  const anchorNet = netOf(anchor);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Confronto fornitori</DialogTitle>
          <DialogDescription>
            Stesso prodotto (o equivalente) presso altri fornitori. Match per codice EAN
            o specifiche tecniche.
          </DialogDescription>
        </DialogHeader>

        {/* Ancora */}
        <div className="rounded-md border bg-primary/5 border-primary/30 p-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Materiale ancora</p>
              <h3 className="text-base font-semibold">{anchor.name}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-muted-foreground">
                <Badge variant="outline" className="font-mono">{anchor.code}</Badge>
                <span>•</span>
                <span>{anchor.supplier ?? '—'}</span>
                {anchor.ean_code && (
                  <>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1">
                      <Barcode className="h-3 w-3" /> {anchor.ean_code}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-muted-foreground">Prezzo netto</p>
              <p className="text-xl font-bold tabular-nums">{euro(anchorNet)}</p>
              <p className="text-xs text-muted-foreground">
                / {anchor.unit ?? 'unità'}
              </p>
            </div>
          </div>
        </div>

        {/* Lista alternative */}
        <div className="flex-1 overflow-auto -mx-6 px-6">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Cerco alternative…
            </div>
          ) : alternatives.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Nessuna alternativa trovata. Verifica che siano stati importati listini di altri fornitori
              con prodotti dello stesso EAN o stesse specifiche tecniche.
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground mb-2">
                <strong>{alternatives.length}</strong>{' '}
                {alternatives.length === 1 ? 'alternativa trovata' : 'alternative trovate'}, ordinate per prezzo crescente.
              </p>
              {alternatives.map((alt) => (
                <AlternativeRow key={alt.id} alt={alt} anchorNet={anchorNet} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AlternativeRow: React.FC<{ alt: MaterialAlternative; anchorNet: number | null }> = ({
  alt, anchorNet,
}) => {
  const altNet = netOf(alt);
  const cheaper = alt.priceDeltaPct != null && alt.priceDeltaPct < -0.1;
  const moreExpensive = alt.priceDeltaPct != null && alt.priceDeltaPct > 0.1;

  return (
    <div className="rounded-md border p-3 hover:bg-muted/30 transition flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-medium text-sm">{alt.name}</h4>
          {alt.matchKind === 'ean' && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">
              <ShieldCheck className="h-2.5 w-2.5 mr-1" />
              EAN match
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-muted-foreground">
          <Badge variant="outline" className="font-mono">{alt.code}</Badge>
          <span>•</span>
          <span className="font-medium">{alt.supplier ?? '—'}</span>
          {alt.ean_code && (
            <>
              <span>•</span>
              <span className="font-mono">{alt.ean_code}</span>
            </>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-base font-semibold tabular-nums">{euro(altNet)}</p>
        {alt.priceDeltaPct != null && anchorNet != null && (
          <span
            className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${
              cheaper ? 'text-green-700' : moreExpensive ? 'text-red-700' : 'text-muted-foreground'
            }`}
            title={`Δ ${euro(alt.priceDeltaAbs)} rispetto all'ancora`}
          >
            {cheaper && <TrendingDown className="h-3 w-3" />}
            {moreExpensive && <TrendingUp className="h-3 w-3" />}
            {alt.priceDeltaPct > 0 ? '+' : ''}{formatNumber(alt.priceDeltaPct, 1)}%
          </span>
        )}
      </div>
    </div>
  );
};

export default MaterialComparatorDialog;
